/**
 * Date formatting helpers.
 *
 * Postgres DATE columns (e.g. approval_date, expiry_date) arrive in the client
 * as date-only 'YYYY-MM-DD' strings. Passing one straight to `new Date(s)`
 * parses it as UTC midnight, which then renders as the PREVIOUS calendar day in
 * negative-UTC timezones (most of the Americas). `formatDateOnly` parses the
 * parts as LOCAL date components instead, so the displayed day never shifts.
 *
 * Use this only for values that are genuinely date-only. Full ISO timestamps
 * (created_at, uploaded_at, last_reminder_sent_at, …) should keep using
 * `new Date(...)` so their time component is honoured.
 */

/**
 * Format a date-only string as a locale date string with no timezone shift.
 *
 * Accepts 'YYYY-MM-DD' and also the leading date portion of an ISO datetime.
 * Returns '' for empty, null, undefined, or unparseable input.
 */
export const formatDateOnly = (value: string | null | undefined): string => {
  if (!value) {
    return '';
  }

  // Use only the date portion so a date-only string and the leading date of an
  // ISO datetime both parse as a local calendar date.
  const datePart = value.split('T')[0];
  const parts = datePart.split('-');
  if (parts.length !== 3) {
    return '';
  }

  const year = Number(parts[0]);
  const month = Number(parts[1]);
  const day = Number(parts[2]);
  if (!Number.isInteger(year) || !Number.isInteger(month) || !Number.isInteger(day)) {
    return '';
  }

  const date = new Date(year, month - 1, day);
  if (Number.isNaN(date.getTime())) {
    return '';
  }

  return date.toLocaleDateString();
};
